// src/components/scambi/ScambiCommunityTab.js
// Community: tab di pari livello con Scambi Liberi e Win-To-Win (prima era un
// sotto-tab annidato dentro Scambi Liberi). Due sezioni impilate: tutti gli
// scambi pubblici ancora votabili (di chiunque, Liberi o Win-To-Win), e solo i
// propri scambi — anche quelli scaduti — con la possibilità di eliminarli
// quando non interessano più.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft,
  CheckCircle2,
  Clock3,
  Globe2,
  Loader2,
  Repeat,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  UserRound,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  castVote,
  deletePublicTrade,
  getMyPublicTrades,
  getOpenPublicTrades,
  isTradeOpen,
  markTradeResultSeen,
} from '../../firebase/publicTradesService';
import TeamBadge from '../TeamBadge';
import Tooltip from '../Tooltip';

function formatTimeRemaining(closesAt) {
  const diff = Number(closesAt || 0) - Date.now();
  if (diff <= 0) return 'in chiusura';
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 1) return 'meno di un\'ora rimasta';
  if (hours < 24) return `${hours}h rimaste`;
  const days = Math.floor(hours / 24);
  return `${days}g rimasti`;
}

const MY_FILTERS = [
  { key: 'all', label: 'Tutti' },
  { key: 'open', label: 'Aperti' },
  { key: 'approved', label: 'Approvati' },
  { key: 'rejected', label: 'Rifiutati' },
];

function tradeMatchesFilter(trade, filterKey) {
  if (filterKey === 'all') return true;
  if (filterKey === 'open') return isTradeOpen(trade);
  if (filterKey === 'approved') return trade.status === 'closed' && trade.result === 'approved';
  if (filterKey === 'rejected') return trade.status === 'closed' && trade.result === 'rejected';
  return true;
}

function TradeKindBadge({ trade }) {
  const kind = trade.kind || 'liberi';
  if (kind === 'wintowin') {
    return (
      <span className="trade-kind-badge trade-kind-badge--wintowin">
        <Repeat size={11} /> Win-To-Win
      </span>
    );
  }
  return (
    <span className="trade-kind-badge trade-kind-badge--liberi">
      <ArrowRightLeft size={11} /> Libero
    </span>
  );
}

function PublicTradeCard({ trade, currentUid, onVote, votingId, onDelete, deletingId }) {
  const open = isTradeOpen(trade);
  const myVote = trade.votesByUid?.[currentUid];
  const isVoting = votingId === trade.id;
  const isDeleting = deletingId === trade.id;
  const isMine = trade.ownerUid === currentUid;

  return (
    <article className="public-trade-card glass">
      <div className="public-trade-card__head">
        <div className="public-trade-card__headLeft">
          <span className="public-trade-card__owner">{trade.ownerDisplayName || 'Un utente FantApp'}</span>
          <TradeKindBadge trade={trade} />
        </div>
        {open ? (
          <span className="public-trade-card__status public-trade-card__status--open">
            <Clock3 size={12} /> {formatTimeRemaining(trade.closesAt)}
          </span>
        ) : trade.result === 'approved' ? (
          <span className="public-trade-card__status public-trade-card__status--approved">
            <CheckCircle2 size={12} /> Approvato
          </span>
        ) : (
          <span className="public-trade-card__status public-trade-card__status--rejected">
            <XCircle size={12} /> Rifiutato
          </span>
        )}
      </div>

      {trade.kind === 'wintowin' && trade.meta?.auctionName && (
        <div className="public-trade-card__context">
          Da: {trade.meta.auctionName}
          {trade.meta.myTeamName && trade.meta.theirTeamName
            ? ` · ${trade.meta.myTeamName} ⇄ ${trade.meta.theirTeamName}`
            : ''}
        </div>
      )}

      <div className="public-trade-card__flow">
        <div className="public-trade-card__side">
          <span>Offre</span>
          {trade.offeredPlayers.map((p) => (
            <span key={p.id} className="public-trade-card__player">
              <TeamBadge team={p.team} size={22} /> {p.name}
            </span>
          ))}
        </div>
        <ArrowRightLeft size={16} className="public-trade-card__arrow" />
        <div className="public-trade-card__side">
          <span>Riceve</span>
          {trade.receivedPlayers.map((p) => (
            <span key={p.id} className="public-trade-card__player">
              <TeamBadge team={p.team} size={22} /> {p.name}
            </span>
          ))}
        </div>
      </div>

      {trade.analysis?.insights?.length > 0 && (
        <ul className="public-trade-card__insights">
          {trade.analysis.insights.slice(0, 2).map((text, idx) => <li key={idx}>{text}</li>)}
        </ul>
      )}

      <div className="public-trade-card__footer">
        <div className="public-trade-card__votes">
          <span className="public-trade-card__voteCount public-trade-card__voteCount--accept">
            <ThumbsUp size={12} /> {trade.acceptCount || 0}
          </span>
          <span className="public-trade-card__voteCount public-trade-card__voteCount--reject">
            <ThumbsDown size={12} /> {trade.rejectCount || 0}
          </span>
        </div>

        <div className="public-trade-card__footerActions">
          {open ? (
            <div className="public-trade-card__voteBtns">
              <button
                type="button"
                className={`public-trade-card__voteBtn public-trade-card__voteBtn--accept${myVote === 'accept' ? ' is-active' : ''}`}
                disabled={isVoting}
                onClick={() => onVote(trade.id, 'accept')}
              >
                <ThumbsUp size={13} /> Accetta
              </button>
              <button
                type="button"
                className={`public-trade-card__voteBtn public-trade-card__voteBtn--reject${myVote === 'reject' ? ' is-active' : ''}`}
                disabled={isVoting}
                onClick={() => onVote(trade.id, 'reject')}
              >
                <ThumbsDown size={13} /> Rifiuta
              </button>
            </div>
          ) : null}

          {isMine && onDelete && (
            <Tooltip
              as="button"
              type="button"
              className="public-trade-card__deleteBtn"
              disabled={isDeleting}
              onClick={() => onDelete(trade.id)}
              aria-label="Elimina scambio"
              content="Elimina scambio"
            >
              {isDeleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
            </Tooltip>
          )}
        </div>
      </div>
    </article>
  );
}

export default function ScambiCommunityTab() {
  const { user } = useAuth();

  const [openTrades, setOpenTrades] = useState([]);
  const [myTrades, setMyTrades] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [votingId, setVotingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [myFilter, setMyFilter] = useState('all');

  const refreshFeed = useCallback(() => {
    setLoadingFeed(true);
    Promise.all([
      getOpenPublicTrades(),
      user?.uid ? getMyPublicTrades(user.uid) : Promise.resolve([]),
    ])
      .then(([open, mine]) => {
        setOpenTrades(open);
        setMyTrades(mine);
      })
      .catch(() => {
        setOpenTrades([]);
        setMyTrades([]);
      })
      .finally(() => setLoadingFeed(false));
  }, [user?.uid]);

  useEffect(() => { refreshFeed(); }, [refreshFeed]);

  // Aprire questa pagina e vedere "i tuoi scambi" chiusi vale come averne
  // visto l'esito: azzera il badge in TopBar.
  useEffect(() => {
    myTrades
      .filter((t) => t.status === 'closed' && !t.ownerSeenResult)
      .forEach((t) => markTradeResultSeen(t.id));
  }, [myTrades]);

  const votableTrades = useMemo(() => openTrades.filter(isTradeOpen), [openTrades]);

  const myCounts = useMemo(() => ({
    all: myTrades.length,
    open: myTrades.filter(isTradeOpen).length,
    approved: myTrades.filter((t) => t.status === 'closed' && t.result === 'approved').length,
    rejected: myTrades.filter((t) => t.status === 'closed' && t.result === 'rejected').length,
  }), [myTrades]);

  const filteredMyTrades = useMemo(
    () => myTrades.filter((t) => tradeMatchesFilter(t, myFilter)),
    [myTrades, myFilter]
  );

  const handleVote = async (tradeId, choice) => {
    if (!user?.uid) return;
    setVotingId(tradeId);
    try {
      await castVote(tradeId, user.uid, choice);
      refreshFeed();
    } finally {
      setVotingId(null);
    }
  };

  const handleDelete = async (tradeId) => {
    if (!window.confirm('Eliminare questo scambio? Non potrà più essere votato e sparirà dalla Community.')) return;
    setDeletingId(tradeId);
    try {
      await deletePublicTrade(tradeId);
      refreshFeed();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="community-tab">
      <section className="community-section glass">
        <div className="community-section__head">
          <div className="community-section__title">
            <Globe2 size={18} />
            <h2>Sondaggi pubblici da votare</h2>
          </div>
          <span className="community-section__count">{votableTrades.length}</span>
        </div>

        {loadingFeed ? (
          <div className="scambi-loading"><Loader2 size={18} className="spin" /> Carico i sondaggi...</div>
        ) : votableTrades.length === 0 ? (
          <div className="trade-target-group__empty">Nessuno scambio pubblico da votare al momento.</div>
        ) : (
          <div className="community-card-grid">
            {votableTrades.map((trade) => (
              <PublicTradeCard
                key={trade.id}
                trade={trade}
                currentUid={user?.uid}
                onVote={handleVote}
                votingId={votingId}
                onDelete={handleDelete}
                deletingId={deletingId}
              />
            ))}
          </div>
        )}
      </section>

      <section className="community-section glass">
        <div className="community-section__head">
          <div className="community-section__title">
            <UserRound size={18} />
            <h2>I tuoi scambi</h2>
          </div>
          <span className="community-section__count">{myCounts.all}</span>
        </div>

        <div className="community-filter-chips">
          {MY_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`community-filter-chip${myFilter === f.key ? ' is-active' : ''}`}
              onClick={() => setMyFilter(f.key)}
            >
              {f.label}
              <span className="community-filter-chip__count">{myCounts[f.key]}</span>
            </button>
          ))}
        </div>

        {loadingFeed ? (
          <div className="scambi-loading"><Loader2 size={18} className="spin" /> Carico i tuoi scambi...</div>
        ) : filteredMyTrades.length === 0 ? (
          <div className="trade-target-group__empty">
            {myTrades.length === 0
              ? 'Non hai ancora reso pubblico nessuno scambio.'
              : 'Nessuno scambio in questa categoria.'}
          </div>
        ) : (
          <div className="community-card-grid">
            {filteredMyTrades.map((trade) => (
              <PublicTradeCard
                key={trade.id}
                trade={trade}
                currentUid={user?.uid}
                onVote={handleVote}
                votingId={votingId}
                onDelete={handleDelete}
                deletingId={deletingId}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
